import {
  GetPartnerRequestsDto,
  PartnerProgramService,
  PartnerRequestStatus,
} from "@/lib/api/partnerProgramService";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "./use-toast";

export const usePartnerRequests = (params: GetPartnerRequestsDto = {}) => {
  return useQuery({
    queryKey: ["/partner-program/requests", params],
    queryFn: () => PartnerProgramService.getRequests(params),
  });
};

export const usePartnerStats = () => {
  return useQuery({
    queryKey: ["/partner-program/requests/stats"],
    queryFn: () => PartnerProgramService.getStats(),
  });
};

export const usePartnerRequest = (id: string | undefined) => {
  return useQuery({
    queryKey: ["/partner-program/requests", id],
    queryFn: () => PartnerProgramService.getRequest(id!),
    enabled: !!id,
  });
};

export const useApprovePartner = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => PartnerProgramService.approve(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/partner-program/requests"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/partner-program/requests/stats"],
      });
      toast({
        title: "Application approved",
        description: "The partner application has been approved successfully.",
      });
      onSuccess?.();
    },
    onError: (error: any) => {
      toast({
        title: "Approval failed",
        description: error.message || "Failed to approve application.",
        variant: "destructive",
      });
    },
  });
};

export const useRejectPartner = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      PartnerProgramService.reject(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/partner-program/requests"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/partner-program/requests/stats"],
      });
      toast({
        title: "Application rejected",
        description: "The partner application has been rejected.",
        variant: "destructive",
      });
      onSuccess?.();
    },
    onError: (error: any) => {
      toast({
        title: "Rejection failed",
        description: error.message || "Failed to reject application.",
        variant: "destructive",
      });
    },
  });
};

export const useRevisionPartner = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      PartnerProgramService.requestRevision(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/partner-program/requests"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/partner-program/requests/stats"],
      });
      toast({
        title: "Revision requested",
        description:
          "The applicant has been asked to revise their application.",
      });
      onSuccess?.();
    },
    onError: (error: any) => {
      toast({
        title: "Revision request failed",
        description: error.message || "Failed to request revision.",
        variant: "destructive",
      });
    },
  });
};

export const useUpdatePartnerStatus = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      id,
      status,
    }: {
      id: string;
      status: PartnerRequestStatus;
    }) => PartnerProgramService.updateStatus(id, status),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["/partner-program/requests"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/partner-program/requests", data.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["/partner-program/requests/stats"],
      });
      toast({
        title: "Status updated",
        description: `Application status changed to ${data.status}.`,
      });
      onSuccess?.();
    },
    onError: (error: any) => {
      toast({
        title: "Status update failed",
        description: error.message || "Failed to update status.",
        variant: "destructive",
      });
    },
  });
};
